import CheckIcon from "@/assets/svgs/check-icon";
import ResponsiveContent from "@/elements/components/shared/responsive-content";
import { AppBar } from "@/elements/components/ui/app-bar";
import { Avatar } from "@/elements/components/ui/avatar";
import { Button } from "@/elements/components/ui/button";
import { Textarea } from "@/elements/components/ui/textarea";
import { api } from "@/elements/components/utils/convex-api";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Id } from "@teachloop/backend/_generated/dataModel";
import { useLocalSearchParams, useRouter } from "expo-router";
import { format, subMinutes, addMinutes } from "date-fns";
import React, { Fragment, useState, useMemo } from "react";
import { Linking, ScrollView, Text, View } from "react-native";
import CloseIcon from "@/assets/svgs/close-icon";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import RequestApprovedModal from "@/elements/components/modals/request-approved-modal";
import RequestRejectedModal from "@/elements/components/modals/request-rejected-modal";
import { useSheet } from "@/elements/components/shared/sheet-provider";
import { SheetIds } from "@/elements/components/utils/sheet-ids";
import goBack from "@/elements/components/utils/go-back";
import BufferTimeSettings from "@/elements/components/shared/buffer-time-settings";

const ReviewRequestFormSchema = z.object({
  tutorNotes: z.string().optional(),
});

function ReviewRequestScreen() {
  const router = useRouter();
  const [modalVisible, setModalVisible] = useState(false);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const { bookingId } = useLocalSearchParams<{ bookingId: Id<"bookings"> }>();
  const [beforeLessonBuffer, setBeforeLessonBuffer] = useState(15);
  const [afterLessonBuffer, setAfterLessonBuffer] = useState(15);
  const { data: booking } = useQuery({
    ...convexQuery(api.tutor.reviewLesson.queries.getBookingRequestDetails, {
      bookingId,
    }),
  });
  const { mutateAsync: approveBooking } = useMutation({
    mutationFn: useConvexMutation(
      api.tutor.reviewLesson.mutations.approveBooking
    ),
  });
  const { mutateAsync: declineBooking } = useMutation({
    mutationFn: useConvexMutation(
      api.tutor.reviewLesson.mutations.declineBooking
    ),
  });
  const { control, handleSubmit, formState } = useForm({
    defaultValues: {
      tutorNotes: "",
    },
    resolver: zodResolver(ReviewRequestFormSchema),
    mode: "onChange",
  });
  const { open } = useSheet(SheetIds.ADDITIONAL_LEARNING);

  const bufferTimes = useMemo(() => {
    if (!booking || booking.deliveryMethod !== "in_person") return null;

    const bufferStartTime = subMinutes(new Date(booking.time.start), beforeLessonBuffer).getTime();
    const bufferEndTime = addMinutes(new Date(booking.time.end), afterLessonBuffer).getTime();

    return { bufferStartTime, bufferEndTime };
  }, [booking, beforeLessonBuffer, afterLessonBuffer]);

  const onApprove = async (data: z.infer<typeof ReviewRequestFormSchema>) => {
    try {
      await approveBooking({
        bookingId,
        tutorNotes: data.tutorNotes,
        bufferStartTime: bufferTimes?.bufferStartTime,
        bufferEndTime: bufferTimes?.bufferEndTime,
      });
      setModalVisible(true);
    } catch (error) {
      console.error(error);
    }
  };

  const onDecline = async (reason: string) => {
    try {
      await declineBooking({
        bookingId,
        declinedReason: reason,
      });
      router.back();
    } catch (error) {
      console.error(error);
    }
  };

  if (!booking) {
    return <Fragment />;
  }

  return (
    <View className="flex-1 bg-teachLoop-creamBase">
      <AppBar>
        <AppBar.Icon onPress={goBack} />
        <AppBar.Title>Back</AppBar.Title>
      </AppBar>
      <ScrollView showsVerticalScrollIndicator={false}>
        <ResponsiveContent>
          <View className="mx-5">
            <View className="h-4" />
            <View className="gap-8">
              <Text className="font-zilla-slab-medium font-medium text-[28px] leading-[1.2] text-black">
                Review request
              </Text>
              <View>
                <Text className="font-geologica-medium font-medium text-xl leading-[1.5] text-gray-800">
                  Summary
                </Text>
                <View className="h-6" />
                <View className="gap-4">
                  <View className="flex-row items-center gap-2">
                    <Avatar size="sm" className="h-12 w-12">
                      <Avatar.Content
                        label={booking.student.name}
                        source={
                          booking.student.imageUrl
                            ? { uri: booking.student.imageUrl }
                            : undefined
                        }
                        labelSize="md"
                        size="md"
                      />
                    </Avatar>
                    <View>
                      <Text className="font-geologica-medium font-medium text-base leading-[1.5] text-gray-800">
                        {booking.student.name}
                      </Text>
                      <Text className="font-geologica-regular font-normal text-sm leading-[1.5] text-gray-800">
                        Parent: {booking.parentName}
                      </Text>
                    </View>
                  </View>
                  {!!booking.notes && (
                    <Text className="font-geologica-regular font-normal text-base leading-[1.5] text-dark">
                      “{booking.notes}”
                    </Text>
                  )}
                  <View className="gap-2">
                    <View className="h-[1px] opacity-10 bg-black" />
                    <View className="gap-2">
                      <View className="flex-row justify-between">
                        <Text className="font-geologica-regular font-normal text-base leading-[1.5] text-gray-600">
                          Subject
                        </Text>
                        <Text className="font-geologica-regular font-normal text-base leading-[1.5] text-teachLoop-textPrimary">
                          {booking.subjectName}
                        </Text>
                      </View>
                      <View className="flex-row justify-between">
                        <Text className="font-geologica-regular font-normal text-base leading-[1.5] text-gray-600">
                          Level
                        </Text>
                        <Text className="font-geologica-regular font-normal text-base leading-[1.5] text-teachLoop-textPrimary">
                          {booking.keyStage}, {booking.yearGroup}
                        </Text>
                      </View>
                      <View className="flex-row justify-between">
                        <Text className="font-geologica-regular font-normal text-base leading-[1.5] text-gray-600">
                          Mode
                        </Text>
                        <Text className="font-geologica-regular font-normal text-base leading-[1.5] text-teachLoop-textPrimary">
                          {booking.type}
                        </Text>
                      </View>
                      <View className="flex-row justify-between">
                        <Text className="font-geologica-regular font-normal text-base leading-[1.5] text-gray-600">
                          Additional learning needs
                        </Text>
                        <Text
                          className="font-geologica-regular font-normal text-base leading-[1.5] text-teachLoop-accentPrimary"
                          onPress={() =>
                            open({
                              additionalLearningNeeds:
                                booking.additionalLearningNeeds,
                            })
                          }
                        >
                          Read more
                        </Text>
                      </View>
                    </View>
                    <View className="h-[1px] opacity-10 bg-black" />
                    <View className="gap-2">
                      <View className="flex-row justify-between">
                        <Text className="font-geologica-regular font-normal text-base leading-[1.5] text-gray-600">
                          Date
                        </Text>
                        <Text className="font-geologica-regular font-normal text-base leading-[1.5] text-teachLoop-textPrimary">
                          {format(booking.time.start, "EEEE, d MMMM")}
                        </Text>
                      </View>
                      <View className="flex-row justify-between">
                        <Text className="font-geologica-regular font-normal text-base leading-[1.5] text-gray-600">
                          Time
                        </Text>
                        <Text className="font-geologica-regular font-normal text-base leading-[1.5] text-teachLoop-textPrimary">
                          {format(booking.time.start, "HH:mm")} -{" "}
                          {format(booking.time.end, "HH:mm")}
                        </Text>
                      </View>
                      {!!booking.location?.address && (
                        <View className="flex-row justify-between">
                          <Text className="font-geologica-regular font-normal text-base leading-[1.5] text-gray-600">
                            Location
                          </Text>
                          <Text
                            className="font-geologica-regular font-normal text-base leading-[1.5] text-teachLoop-textPrimary underline"
                            onPress={() => {
                              Linking.openURL(
                                `https://www.google.com/maps/search/?api=1&query=${booking.location?.address}`
                              );
                            }}
                          >
                            {booking.location?.address}
                          </Text>
                        </View>
                      )}
                    </View>
                    <View className="h-[1px] opacity-10 bg-black" />
                    <View className="gap-2">
                      <View className="flex-row justify-between">
                        <Text className="font-geologica-regular font-normal text-base leading-[1.5] text-gray-600">
                          Lesson earnings
                        </Text>
                        <Text className="font-geologica-regular font-normal text-base leading-[1.5] text-teachLoop-textPrimary">
                          AED {booking.lessonEarnings}
                        </Text>
                      </View>
                      {booking.travelCostCoverage > 0 && (
                        <View className="flex-row justify-between">
                          <Text className="font-geologica-regular font-normal text-base leading-[1.5] text-gray-600">
                            Travel cost coverage
                          </Text>
                          <Text className="font-geologica-regular font-normal text-base leading-[1.5] text-teachLoop-textPrimary">
                            AED {booking.travelCostCoverage}
                          </Text>
                        </View>
                      )}
                      <View className="flex-row justify-between">
                        <Text className="font-geologica-regular font-normal text-xl leading-[1.5] text-gray-600">
                          Total earnings
                        </Text>
                        <Text className="font-geologica-regular font-normal text-xl leading-[1.5] text-teachLoop-textPrimary">
                          AED {booking.totalEarnings}
                        </Text>
                      </View>
                    </View>
                  </View>
                </View>
              </View>
              {booking.deliveryMethod === "in_person" && (
                <BufferTimeSettings
                  beforeLessonBuffer={beforeLessonBuffer}
                  afterLessonBuffer={afterLessonBuffer}
                  setBeforeLessonBuffer={setBeforeLessonBuffer}
                  setAfterLessonBuffer={setAfterLessonBuffer}
                  originalStartTime={booking.time.start}
                  originalEndTime={booking.time.end}
                  bookingId={bookingId}
                />
              )}
              <View className="gap-4">
                <Text className="font-geologica-medium font-medium text-xl leading-[1.5] text-black">
                  Add a note (optional)
                </Text>
                <Controller
                  control={control}
                  name="tutorNotes"
                  render={({ field, fieldState: { error } }) => (
                    <Textarea
                      placeholder="Add a personal message to the parent or student.. "
                      className="h-[150]"
                      size="sm"
                      multiline
                      numberOfLines={4}
                      textAlignVertical="top"
                      {...field}
                      error={!!error?.message}
                    />
                  )}
                />
              </View>
              <View className="gap-4">
                <Button
                  size="md"
                  intent="primary"
                  fullWidth
                  icon
                  disabled={!formState.isValid}
                  onPress={handleSubmit(onApprove)}
                >
                  <Button.Icon as={CheckIcon} pathClassName="stroke-white" />
                  <Button.Text size="md">
                    Accept and add to calendar
                  </Button.Text>
                </Button>
                <Button
                  size="md"
                  intent="secondary"
                  fullWidth
                  outline
                  icon
                  onPress={() => {
                    setRejectModalVisible(true);
                  }}
                >
                  <Button.Icon
                    as={CloseIcon}
                    intent="danger"
                    pathClassName="stroke-red-500"
                  />
                  <Button.Text size="md" intent="danger">
                    Decline request
                  </Button.Text>
                </Button>
              </View>
              <View className="h-8" />
            </View>
          </View>
        </ResponsiveContent>
      </ScrollView>

      <RequestApprovedModal
        isVisible={modalVisible}
        onClose={() => setModalVisible(false)}
        onDismiss={() => {
          router.back();
        }}
      />

      <RequestRejectedModal
        isVisible={rejectModalVisible}
        onClose={() => setRejectModalVisible(false)}
        onDismiss={(reason) => {
          if (reason) {
            onDecline(reason);
          }
        }}
      />
    </View>
  );
}

export default ReviewRequestScreen;
